import React, { useState, useCallback, useEffect, useImperativeHandle, forwardRef } from "react";

interface Player {
  id: number;
  name: string;
  email: string;
  position?: string[] | null;
  specific_positions?: string[] | null;
  age?: number | null;
  height?: string | null;
  weight?: string | null;
  nationality?: string | null;
  current_club?: string | null;
  playing_level?: string | null;
  gpa?: string | null;
  hs_grad_year?: number | null;
  university_grad_year?: number | null;
  previous_clubs?: string[] | null;
  international_caps?: any[] | null;
  distinguishments?: string[] | null;
  highlight_videos?: string[] | null;
  attachments?: string[] | null;
  date_added?: string | null;
  email_content?: string | null;
}

interface EmailVariables {
  PlayerName: string;
  PlayerFirstName: string;
  PlayerLastName: string;
  PlayerPosition: string;
  PlayerClub: string;
  PlayerLevel: string;
}

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  selectedPlayer?: Player | null;
  variables?: EmailVariables;
  style?: React.CSSProperties;
  className?: string;
}


const RichTextEditor = forwardRef(function RichTextEditor({ 
  content, 
  onChange, 
  placeholder = "Write your message here...",
  selectedPlayer,
  variables,
  style,
  className
}: RichTextEditorProps, ref) {
  const [isPreview, setIsPreview] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [textAreaHeight, setTextAreaHeight] = useState(200);

  // Handle manual resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    
    const startY = e.clientY;
    const startHeight = textAreaHeight;
    
    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const newHeight = Math.max(100, Math.min(600, startHeight + deltaY)); // Min 100px, max 600px
      setTextAreaHeight(newHeight);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [textAreaHeight]);

  const handleFormatting = useCallback((format: string) => {
    const textarea = document.querySelector('[data-rich-text-area]') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let formattedText = '';
    let newContent = '';
    let newCursorPos = start;

    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        newContent = content.substring(0, start) + formattedText + content.substring(end);
        newCursorPos = selectedText ? end + 4 : start + 2;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        newContent = content.substring(0, start) + formattedText + content.substring(end);
        newCursorPos = selectedText ? end + 2 : start + 1;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        newContent = content.substring(0, start) + formattedText + content.substring(end);
        newCursorPos = selectedText ? end + 4 : start + 2;
        break;
      case 'link':
        formattedText = selectedText ? `[${selectedText}](url)` : '[Link Text](url)';
        newContent = content.substring(0, start) + formattedText + content.substring(end);
        newCursorPos = selectedText ? end + 6 : start + 11;
        break;
      case 'list':
      case 'ordered-list':
        // Get the current line(s) that are selected or where cursor is
        const beforeCursor = content.substring(0, start);
        const afterCursor = content.substring(end);
        const selectedContent = start === end ? '' : content.substring(start, end);
        
        // Split content into lines and find affected lines
        const allLines = content.split('\n');
        const beforeLines = beforeCursor.split('\n');
        
        // Determine which lines are affected
        let startLineIndex = beforeLines.length - 1;
        let endLineIndex = startLineIndex;
        
        if (start !== end) {
          // Calculate how many lines are selected
          const selectedLines = selectedContent.split('\n');
          endLineIndex = startLineIndex + selectedLines.length - 1;
        }
        
        // Check if all affected lines already have the requested format
        const affectedLines = allLines.slice(startLineIndex, endLineIndex + 1);
        const isBulletFormat = format === 'list';
        const isOrderedFormat = format === 'ordered-list';
        
        let shouldRemoveFormat = false;
        if (isBulletFormat) {
          // Check if all affected lines are already bullet points
          shouldRemoveFormat = affectedLines.every(line => 
            /^(\s*)• /.test(line) || line.trim() === ''
          ) && affectedLines.some(line => /^(\s*)• /.test(line));
        } else {
          // Check if all affected lines are already numbered
          shouldRemoveFormat = affectedLines.every(line => 
            /^(\s*)\d+\. /.test(line) || line.trim() === ''
          ) && affectedLines.some(line => /^(\s*)\d+\. /.test(line));
        }
        
        // Process all lines
        const modifiedLines = allLines.map((line, index) => {
          // Only modify lines that are affected
          if (index < startLineIndex || index > endLineIndex) {
            return line;
          }
          
          const isBulletList = /^(\s*)• /.test(line);
          const isNumberedList = /^(\s*)\d+\. /.test(line);
          const indentation = line.match(/^(\s*)/)?.[1] || '';
          
          if (shouldRemoveFormat) {
            // Remove formatting but preserve indentation
            if (isBulletFormat && isBulletList) {
              return line.replace(/^(\s*)• /, '$1');
            } else if (isOrderedFormat && isNumberedList) {
              return line.replace(/^(\s*)\d+\. /, '$1');
            }
            return line;
          } else {
            // Prevent mixing list types on the same line/level
            // If adding bullets, convert any numbered lists to bullets
            // If adding numbers, convert any bullet lists to numbers
            if (isBulletFormat) {
              if (isNumberedList) {
                // Convert numbered to bullet, preserve indentation
                return line.replace(/^(\s*)\d+\. /, '$1• ');
              } else if (!isBulletList) {
                // Add bullet, preserve any existing indentation
                return indentation + '• ' + line.substring(indentation.length);
              }
            } else { // ordered-list
              if (isBulletList) {
                // Convert bullet to numbered, preserve indentation
                return line.replace(/^(\s*)• /, '$11. ');
              } else if (!isNumberedList) {
                // Add numbering, preserve any existing indentation
                return indentation + '1. ' + line.substring(indentation.length);
              }
            }
          }
          
          return line;
        });
        
        // Renumber ordered lists to be sequential by indentation level
        if (isOrderedFormat && !shouldRemoveFormat) {
          const renumberedLines = modifiedLines.map(line => {
            if (/^(\s*)\d+\. /.test(line)) {
              const indentMatch = line.match(/^(\s*)/);
              const indentLevel = indentMatch ? indentMatch[1].length : 0;
              
              // Find the correct number for this indentation level
              let numberForLevel = 1;
              const lineIndex = modifiedLines.indexOf(line);
              
              // Count previous items at the same indentation level
              for (let i = 0; i < lineIndex; i++) {
                const prevLine = modifiedLines[i];
                const prevIndentMatch = prevLine.match(/^(\s*)\d+\. /);
                if (prevIndentMatch && prevIndentMatch[1].length === indentLevel) {
                  numberForLevel++;
                }
              }
              
              return line.replace(/^(\s*)\d+\. /, `$1${numberForLevel}. `);
            }
            return line;
          });
          newContent = renumberedLines.join('\n');
        } else {
          newContent = modifiedLines.join('\n');
        }
        
        // Calculate proper cursor position for list formatting
        if (shouldRemoveFormat) {
          // When removing formatting, adjust cursor position based on what was removed
          const originalLine = allLines[startLineIndex];
          if (isBulletFormat && /^• /.test(originalLine)) {
            newCursorPos = Math.max(0, start - 2); // Account for removed "• "
          } else if (isOrderedFormat && /^\d+\. /.test(originalLine)) {
            const numberMatch = originalLine.match(/^(\d+\. )/);
            if (numberMatch) {
              newCursorPos = Math.max(0, start - numberMatch[1].length);
            }
          } else {
            newCursorPos = start;
          }
        } else {
          // When adding formatting, adjust cursor position based on what was added
          const beforeCursor = content.substring(0, start);
          const beforeLines = beforeCursor.split('\n');
          const currentLineIndex = beforeLines.length - 1;
          const currentLineStart = beforeCursor.lastIndexOf('\n') + 1;
          const cursorPositionInLine = start - currentLineStart;
          
          if (isBulletFormat) {
            newCursorPos = start + 2; // Account for added "• "
          } else { // ordered list
            newCursorPos = start + 3; // Account for added "1. "
          }
        }
        break;
      default:
        return;
    }

    onChange(newContent);
    
    // Restore cursor position after state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [content, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const { selectionStart: start, selectionEnd: end } = textarea;
    
    // Handle Tab for indentation in lists
    if (e.key === 'Tab') {
      const beforeCursor = content.substring(0, start);
      const afterCursor = content.substring(end);
      const lines = beforeCursor.split('\n');
      const currentLine = lines[lines.length - 1];
      
      // Check if we're in a list item
      const bulletMatch = currentLine.match(/^(\s*)(• )/);
      const numberMatch = currentLine.match(/^(\s*)(\d+\. )/);
      
      if (bulletMatch || numberMatch) {
        e.preventDefault();
        
        if (e.shiftKey) {
          // Shift+Tab: Decrease indentation (unindent)
          if (bulletMatch && bulletMatch[1].length >= 2) {
            const newIndent = bulletMatch[1].substring(2); // Remove 2 spaces
            const newLine = newIndent + bulletMatch[2] + currentLine.substring(bulletMatch[1].length + bulletMatch[2].length);
            const newContent = lines.slice(0, -1).join('\n') + (lines.length > 1 ? '\n' : '') + newLine + afterCursor;
            onChange(newContent);
            setTimeout(() => {
              textarea.setSelectionRange(start - 2, start - 2);
            }, 0);
          } else if (numberMatch && numberMatch[1].length >= 2) {
            const newIndent = numberMatch[1].substring(2); // Remove 2 spaces
            const newLine = newIndent + numberMatch[2] + currentLine.substring(numberMatch[1].length + numberMatch[2].length);
            const newContent = lines.slice(0, -1).join('\n') + (lines.length > 1 ? '\n' : '') + newLine + afterCursor;
            onChange(newContent);
            setTimeout(() => {
              textarea.setSelectionRange(start - 2, start - 2);
            }, 0);
          }
        } else {
          // Tab: Increase indentation (indent)
          if (bulletMatch) {
            const newIndent = bulletMatch[1] + '  '; // Add 2 spaces
            const newLine = newIndent + bulletMatch[2] + currentLine.substring(bulletMatch[1].length + bulletMatch[2].length);
            const newContent = lines.slice(0, -1).join('\n') + (lines.length > 1 ? '\n' : '') + newLine + afterCursor;
            onChange(newContent);
            setTimeout(() => {
              textarea.setSelectionRange(start + 2, start + 2);
            }, 0);
          } else if (numberMatch) {
            const newIndent = numberMatch[1] + '  '; // Add 2 spaces
            const newLine = newIndent + numberMatch[2] + currentLine.substring(numberMatch[1].length + numberMatch[2].length);
            const newContent = lines.slice(0, -1).join('\n') + (lines.length > 1 ? '\n' : '') + newLine + afterCursor;
            onChange(newContent);
            setTimeout(() => {
              textarea.setSelectionRange(start + 2, start + 2);
            }, 0);
          }
        }
        return;
      }
    }
    
    if (e.key === 'Enter') {
      // Get current line
      const beforeCursor = content.substring(0, start);
      const afterCursor = content.substring(end);
      const lines = beforeCursor.split('\n');
      const currentLine = lines[lines.length - 1];
      
      // Check if we're in a list (with possible indentation)
      const bulletMatch = currentLine.match(/^(\s*)(• )/);
      const numberMatch = currentLine.match(/^(\s*)(\d+\. )/);
      
      if (bulletMatch) {
        e.preventDefault();
        const indent = bulletMatch[1];
        const newContent = beforeCursor + '\n' + indent + '• ' + afterCursor;
        onChange(newContent);
        setTimeout(() => {
          textarea.setSelectionRange(start + 3 + indent.length, start + 3 + indent.length);
        }, 0);
        return;
      }
      
      if (numberMatch) {
        e.preventDefault();
        const indent = numberMatch[1];
        const currentNumber = parseInt(numberMatch[2]);
        const nextNumber = currentNumber + 1;
        const newContent = beforeCursor + '\n' + indent + `${nextNumber}. ` + afterCursor;
        onChange(newContent);
        setTimeout(() => {
          const newPos = start + `\n${indent}${nextNumber}. `.length;
          textarea.setSelectionRange(newPos, newPos);
        }, 0);
        return;
      }
    }
    
    // Handle backspace to exit lists
    if (e.key === 'Backspace') {
      const beforeCursor = content.substring(0, start);
      const lines = beforeCursor.split('\n');
      const currentLine = lines[lines.length - 1];
      
      // If we're at the start of a list item and it's empty, remove the list formatting
      if (start === end && (/^(\s*)• $/.test(currentLine) || /^(\s*)\d+\. $/.test(currentLine))) {
        e.preventDefault();
        const newLines = [...lines];
        newLines[newLines.length - 1] = '';
        const newContent = newLines.join('\n') + content.substring(end);
        onChange(newContent);
        setTimeout(() => {
          const newPos = start - currentLine.length;
          textarea.setSelectionRange(newPos, newPos);
        }, 0);
        return;
      }
    }
  }, [content, onChange]);

  // Helper function to split player name
  const splitPlayerName = (fullName: string): { firstName: string; lastName: string } => {
    if (!fullName || !fullName.trim()) {
      return { firstName: '', lastName: '' };
    }
    
    const nameParts = fullName.trim().split(' ');
    
    if (nameParts.length === 1) {
      // Only one name provided
      return { firstName: nameParts[0], lastName: '' };
    } else if (nameParts.length === 2) {
      // First and last name
      return { firstName: nameParts[0], lastName: nameParts[1] };
    } else {
      // Multiple names - first is firstName, rest is lastName
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');
      return { firstName, lastName };
    }
  };

  // Auto-resize textarea only if no external height is provided
  const adjustTextAreaHeight = useCallback(() => {
    if (style?.height) return; // Don't auto-resize if external height is set
    const textarea = document.querySelector('[data-rich-text-area]') as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(200, textarea.scrollHeight) + 'px';
    }
  }, [style?.height]);

  // Auto-resize on content change (only if no external height)
  useEffect(() => {
    if (!style?.height) {
      adjustTextAreaHeight();
    }
  }, [content, adjustTextAreaHeight, style?.height]);

  // Insert variable function
  const insertVariable = (variable: string) => {
    const textarea = document.querySelector('[data-rich-text-area]') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const variableText = `{{${variable}}}`;
    const newContent = content.substring(0, start) + variableText + content.substring(start);
    onChange(newContent);
    setShowVariables(false);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variableText.length, start + variableText.length);
    }, 0);
  };

  // Expose insertVariable to parent via ref
  useImperativeHandle(ref, () => ({
    insertVariable,
  }));

  // Function to replace variables with actual values
  const replaceVariables = (text: string) => {
    if (!selectedPlayer && !variables) return text;
    
    // Create dynamic variables from selected player or passed variables
    const dynamicVariables: EmailVariables = variables || {
      PlayerName: selectedPlayer?.name || 'Player Name',
      PlayerFirstName: splitPlayerName(selectedPlayer?.name || '').firstName || 'First Name',
      PlayerLastName: splitPlayerName(selectedPlayer?.name || '').lastName || 'Last Name',
      PlayerPosition: (selectedPlayer?.position && Array.isArray(selectedPlayer.position) && selectedPlayer.position.length > 0) ? selectedPlayer.position.join(', ') : 'Position',
      PlayerClub: selectedPlayer?.current_club || 'Current Club',
      PlayerLevel: selectedPlayer?.playing_level || 'Playing Level',
    };

    return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
      const key = variableName as keyof EmailVariables;
      return dynamicVariables[key] || match;
    });
  };

  const formatPreview = (text: string) => {
    // First replace variables with actual values for preview
    const textWithReplacedVariables = replaceVariables(text);
    
    // Process the text line by line to handle lists properly
    const lines = textWithReplacedVariables.split('\n');
    const processedLines: string[] = [];
    let inBulletList = false;
    let inNumberedList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check for bullet list items
      const bulletMatch = line.match(/^(\s*)• (.+)$/);
      // Check for numbered list items  
      const numberMatch = line.match(/^(\s*)(\d+)\. (.+)$/);
      
      if (bulletMatch) {
        const indentLevel = bulletMatch[1].length / 2;
        const itemText = bulletMatch[2];
        
        if (!inBulletList) {
          if (inNumberedList) {
            processedLines.push('</ol>');
            inNumberedList = false;
          }
          processedLines.push('<ul class="list-disc ml-6 my-2">');
          inBulletList = true;
        }
        
        processedLines.push(`<li class="ml-${indentLevel * 4}">${itemText}</li>`);
      } else if (numberMatch) {
        const indentLevel = numberMatch[1].length / 2;
        const itemText = numberMatch[3];
        
        if (!inNumberedList) {
          if (inBulletList) {
            processedLines.push('</ul>');
            inBulletList = false;
          }
          processedLines.push('<ol class="list-decimal ml-6 my-2">');
          inNumberedList = true;
        }
        
        processedLines.push(`<li class="ml-${indentLevel * 4}">${itemText}</li>`);
      } else {
        // Close any open lists
        if (inBulletList) {
          processedLines.push('</ul>');
          inBulletList = false;
        }
        if (inNumberedList) {
          processedLines.push('</ol>');
          inNumberedList = false;
        }
        
        // Process regular line for other formatting
        if (line.trim()) {
          // Apply inline formatting to non-list lines
          let formattedLine = line
            // Bold: **text** -> <strong>text</strong>
            .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
            // Italic: *text* -> <em>text</em>  
            .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic">$1</em>')
            // Underline: __text__ -> <u>text</u>
            .replace(/__(.*?)__/g, '<u class="underline">$1</u>')
            // Links: [text](url) -> <a href="url">text</a>
            .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 underline hover:text-blue-800" target="_blank" rel="noopener noreferrer">$1</a>');
          
          processedLines.push(`<p class="mb-2">${formattedLine}</p>`);
        } else {
          processedLines.push('<div class="mb-2"></div>'); // Empty line spacing
        }
      }
    }
    
    // Close any remaining open lists
    if (inBulletList) {
      processedLines.push('</ul>');
    }
    if (inNumberedList) {
      processedLines.push('</ol>');
    }
    
    // Join the processed lines
    let result = processedLines.join('\n');
    
    // Apply formatting to list items as well
    result = result
      // Bold: **text** -> <strong>text</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
      // Italic: *text* -> <em>text</em>  
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic">$1</em>')
      // Underline: __text__ -> <u>text</u>
      .replace(/__(.*?)__/g, '<u class="underline">$1</u>')
      // Links: [text](url) -> <a href="url">text</a>
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 underline hover:text-blue-800" target="_blank" rel="noopener noreferrer">$1</a>');
    
    return result;
  };

  // Helper function to check if current line/selection has list formatting
  const getCurrentListState = () => {
    const textarea = document.querySelector('[data-rich-text-area]') as HTMLTextAreaElement;
    if (!textarea) return { hasBullets: false, hasNumbers: false };

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const beforeCursor = content.substring(0, start);
    
    const allLines = content.split('\n');
    const beforeLines = beforeCursor.split('\n');
    let startLineIndex = beforeLines.length - 1;
    let endLineIndex = startLineIndex;
    
    if (start !== end) {
      const selectedContent = content.substring(start, end);
      const selectedLines = selectedContent.split('\n');
      endLineIndex = startLineIndex + selectedLines.length - 1;
    }
    
    const affectedLines = allLines.slice(startLineIndex, endLineIndex + 1);
    const hasBullets = affectedLines.some(line => /^(\s*)• /.test(line));
    const hasNumbers = affectedLines.some(line => /^(\s*)\d+\. /.test(line));
    
    return { hasBullets, hasNumbers };
  };

  const listState = getCurrentListState();

  // Show variable values in dropdown when player is selected
  const getVariablePreview = (variableName: string) => {
    if (!selectedPlayer && !variables) return `{{${variableName}}}`;
    
    const dynamicVariables: EmailVariables = variables || {
      PlayerName: selectedPlayer?.name || 'Player Name',
      PlayerFirstName: splitPlayerName(selectedPlayer?.name || '').firstName || 'First Name',
      PlayerLastName: splitPlayerName(selectedPlayer?.name || '').lastName || 'Last Name',
      PlayerPosition: (selectedPlayer?.position && Array.isArray(selectedPlayer.position) && selectedPlayer.position.length > 0) ? selectedPlayer.position.join(', ') : 'Position',
      PlayerClub: selectedPlayer?.current_club || 'Current Club',
      PlayerLevel: selectedPlayer?.playing_level || 'Playing Level',
    };

    const key = variableName as keyof EmailVariables;
    return dynamicVariables[key] || `{{${variableName}}}`;
  };

  return (
    <div 
      className={`border border-gray-300 rounded-lg w-full max-w-full overflow-hidden ${className || ''} ${
        style?.height ? 'flex flex-col' : ''
      }`}
      style={style}
    >
      {/* Player Info Banner */}
      {selectedPlayer && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex-shrink-0">
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-blue-800 font-medium">
              Preview for: {selectedPlayer.name}
            </span>
            {selectedPlayer.position && Array.isArray(selectedPlayer.position) && (
              <span className="text-blue-600">• {selectedPlayer.position.join(', ')}</span>
            )}
            {selectedPlayer.age && (
              <span className="text-blue-600">• {selectedPlayer.age}y</span>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-gray-50 px-2 sm:px-4 py-2 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2 flex-shrink-0">
        <div className="flex items-center flex-wrap gap-1">
          <button
            type="button"
            onClick={() => handleFormatting('bold')}
            className="h-8 w-8 p-0 inline-flex items-center justify-center rounded hover:bg-gray-200 flex-shrink-0 group"
            title="Bold (**text**)"
          >
            <i className="fas fa-bold text-sm"></i>
          </button>
          <button
            type="button"
            onClick={() => handleFormatting('italic')}
            className="h-8 w-8 p-0 inline-flex items-center justify-center rounded hover:bg-gray-200 flex-shrink-0"
            title="Italic (*text*)"
          >
            <i className="fas fa-italic text-sm"></i>
          </button>
          <button
            type="button"
            onClick={() => handleFormatting('underline')}
            className="h-8 w-8 p-0 inline-flex items-center justify-center rounded hover:bg-gray-200 flex-shrink-0"
            title="Underline (__text__)"
          >
            <i className="fas fa-underline text-sm"></i>
          </button>
          
          <div className="w-px h-4 bg-gray-300 mx-1"></div>
          
          <button
            type="button"
            onClick={() => handleFormatting('list')}
            className={`h-8 w-8 p-0 inline-flex items-center justify-center rounded flex-shrink-0 transition-colors ${
              listState.hasBullets 
                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                : 'hover:bg-gray-200'
            }`}
            title="Bullet List (• item)"
          >
            <i className="fas fa-list-ul text-sm"></i>
          </button>
          <button
            type="button"
            onClick={() => handleFormatting('ordered-list')}
            className={`h-8 w-8 p-0 inline-flex items-center justify-center rounded flex-shrink-0 transition-colors ${
              listState.hasNumbers 
                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                : 'hover:bg-gray-200'
            }`}
            title="Numbered List (1. item)"
          >
            <i className="fas fa-list-ol text-sm"></i>
          </button>
          <button
            type="button"
            onClick={() => handleFormatting('link')}
            className="h-8 w-8 p-0 inline-flex items-center justify-center rounded hover:bg-gray-200 flex-shrink-0"
            title="Link ([text](url))"
          >
            <i className="fas fa-link text-sm"></i>
          </button>
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => setIsPreview(!isPreview)}
            className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${
              isPreview 
                ? 'bg-primary text-white hover:bg-primary/80' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {isPreview ? 'Edit' : 'Preview'}
          </button>
          
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowVariables(!showVariables)}
              className="text-xs px-2 py-1 rounded hover:bg-gray-200 inline-flex items-center"
            >
              <i className="fas fa-code text-xs mr-1"></i>
              Variables
            </button>
            
            {/* Variables Dropdown */}
            {showVariables && (
              <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
                <div className="p-2 space-y-1">
                  {[
                    'PlayerName',
                    'PlayerFirstName',
                    'PlayerLastName',
                    'PlayerPosition', 
                    'PlayerClub',
                    'PlayerLevel'
                  ].map((variable) => (
                    <button
                      key={variable}
                      type="button"
                      onClick={() => insertVariable(variable)}
                      className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 rounded flex items-center justify-between"
                    >
                      <code className="bg-gray-200 px-1 py-0.5 rounded text-xs">
                        {`{{${variable}}}`}
                      </code>
                      {(selectedPlayer || variables) && (
                        <span className="text-green-600 text-xs font-medium ml-2 truncate">
                          → {getVariablePreview(variable)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Area */}
      {isPreview ? (
        <div 
          className={`px-4 py-3 bg-white w-full overflow-y-auto prose prose-sm max-w-none ${
            style?.height ? 'flex-1' : ''
          }`}
          style={style?.height ? {} : { minHeight: `${textAreaHeight}px` }}
        >
          <div 
            dangerouslySetInnerHTML={{ __html: formatPreview(content) }}
            style={{ 
              fontFamily: 'inherit',
              lineHeight: '1.6'
            }}
          />
        </div>
      ) : (
        <div className={`relative ${style?.height ? 'flex-1 flex flex-col' : ''}`}>
          <textarea
            data-rich-text-area
            value={content}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`w-full px-4 py-3 border-none outline-none text-sm leading-relaxed focus:ring-0 font-mono ${
              style?.height ? 'resize-none overflow-y-auto flex-1' : 'resize-none'
            }`}
            style={style?.height ? {} : { height: `${textAreaHeight}px` }}
          />
          
          {/* Helpful formatting hints */}
          <div className={`absolute bottom-2 right-6 text-xs text-gray-400 bg-white/80 backdrop-blur-sm rounded px-2 py-1 ${
            style?.height ? 'pointer-events-none' : ''
          }`}>
            <div className="flex gap-3 text-xs">
              <span><strong>**bold**</strong></span>
              <span><em>*italic*</em></span>
              <span><u>__underline__</u></span>
            </div>
          </div>
          
          {/* Resize handle - only show if no external height is set */}
          {!style?.height && (
            <div 
              className={`absolute bottom-0 right-0 w-4 h-4 cursor-se-resize group ${
                isResizing ? 'bg-blue-300' : 'bg-gray-200 hover:bg-blue-200'
              } border-l-2 border-t-2 ${
                isResizing ? 'border-blue-400' : 'border-gray-400 group-hover:border-blue-400'
              } transition-all duration-150`}
              onMouseDown={handleMouseDown}
              style={{
                clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)'
              }}
            >
            {/* More visible resize grip lines */}
            <div className="absolute bottom-0.5 right-0.5 w-3 h-3 pointer-events-none">
              <div className={`absolute bottom-0 right-0 w-full h-0.5 ${
                isResizing ? 'bg-blue-600' : 'bg-gray-600 group-hover:bg-blue-600'
              } transition-colors`}></div>
              <div className={`absolute bottom-1 right-0 w-full h-0.5 ${
                isResizing ? 'bg-blue-600' : 'bg-gray-600 group-hover:bg-blue-600'
              } transition-colors`}></div>
              <div className={`absolute bottom-0 right-0 w-0.5 h-full ${
                isResizing ? 'bg-blue-600' : 'bg-gray-600 group-hover:bg-blue-600'
              } transition-colors`}></div>
              <div className={`absolute bottom-0 right-1 w-0.5 h-full ${
                isResizing ? 'bg-blue-600' : 'bg-gray-600 group-hover:bg-blue-600'
              } transition-colors`}></div>
            </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default RichTextEditor;